import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, ArrowLeft } from 'lucide-react';
import NomineeCard from './NomineeCard';
import PaymentModal from './PaymentModal';
import VotingHeader from './VotingHeader';
import { getRootSurvey } from '../api/voting';

function buildVotingCode(survey, category, nominee) {
  const pad2 = (n) => String(n ?? '').padStart(2, '0');
  return pad2(category.code) + pad2(nominee.code);
}

export default function NomineeView({ code, settings: settingsProp }) {
  const [nominee, setNominee]     = useState(null);
  const [survey, setSurvey]       = useState(null);
  const [settings, setSettings]   = useState(settingsProp || null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [voteTarget, setVoteTarget] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await getRootSurvey();
        const s    = res?.data?.survey;
        const cats = res?.data?.categories || [];
        const cfg  = res?.data?.settings;
        setSurvey(s);
        setSettings(cfg);

        if (cfg) {
          const root = document.documentElement;
          if (cfg.primary_color)    root.style.setProperty('--color-primary',    cfg.primary_color);
          if (cfg.secondary_color)  root.style.setProperty('--color-secondary',  cfg.secondary_color);
          if (cfg.background_color) {
            root.style.setProperty('--color-background', cfg.background_color);
            document.body.style.backgroundColor = cfg.background_color;
          }
          if (cfg.text_color)       root.style.setProperty('--color-text', cfg.text_color);
        }

        let found = null;
        for (const c of cats) {
          for (const n of (c.nominees || [])) {
            const vc = buildVotingCode(s, c.category, n);
            if (vc === code) {
              found = {
                id:           n.id,
                name:         n.nominee,
                photo:        n.photo || null,
                votingCode:   vc,
                categoryId:   c.category.id,
                categoryName: c.category.categoryname,
              };
              break;
            }
          }
          if (found) break;
        }

        if (!found) setError('Nominee not found for this voting code.');
        else setNominee(found);
      } catch (e) {
        setError(e?.message || 'Failed to load.');
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <div className="content-voting min-vh-100 d-flex flex-column">
      <div className="container-xl px-3 px-md-4 pt-3 pb-3 flex-grow-1">

        <VotingHeader survey={survey} settings={settings} />

        <a href="/" className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center gap-1 mb-4 mx-auto" style={{ width: 'fit-content' }}>
          <ArrowLeft size={14} /> View All Nominees
        </a>

        {loading && (
          <div className="d-flex flex-column align-items-center text-muted py-5 gap-2">
            <Loader2 size={32} className="text-warning spin" />
            <p className="fw-medium mb-0">Loading nominee…</p>
          </div>
        )}

        {error && !loading && (
          <div className="mx-auto text-center p-4 rounded-3 border border-danger-subtle bg-danger-subtle" style={{ maxWidth: 420 }}>
            <AlertTriangle size={28} className="text-danger mb-2" />
            <p className="fw-semibold mb-1">Nominee not found</p>
            <p className="small text-muted mb-0">{error}</p>
          </div>
        )}

        {!loading && nominee && (
          <div className="row justify-content-center">
            <div className="col-10 col-sm-7 col-md-5 col-lg-4 col-xl-3">
              <NomineeCard
                nominee={nominee}
                onVote={(n, votes) => setVoteTarget({ nominee: n, votes })}
              />
            </div>
          </div>
        )}

      </div>

      <footer className="text-center py-3 small credits-text">
        Powered by <a href="https://etuzo.co.ke" className="credits-link">eTuzo</a>
      </footer>

      {voteTarget && survey && (
        <PaymentModal
          open={!!voteTarget}
          onClose={() => setVoteTarget(null)}
          nominee={voteTarget.nominee}
          votes={voteTarget.votes}
          survey={survey}
        />
      )}
    </div>
  );
}
